/* Judge.me owns review data; the Listing rating selector delegates to its histogram. */
(() => {
  if (!document.documentElement.classList.contains('listing-page') || window.listingReviewsSkin) return;
  window.listingReviewsSkin = true;
  const ratingRows = widget => [...widget.querySelectorAll('.jm-histogram-row[role="button"]')];
  const rowRating = row => row.querySelector('.jm-histogram-row__rating')?.textContent.trim();
  const selectedRating = widget => widget.querySelector('.jm-rating-filter__tag--selected input')?.value || '';
  // A partial theme sync can load this script with the previous translation snippet.
  // Use the active page language until the snippet supplies its configurable strings.
  const defaultReviewStrings = /^it(?:-|$)/i.test(document.documentElement.lang) ? {
    reviewFilterBy: 'Filtra per',
    reviewAllRatings: 'Tutte le valutazioni',
    reviewStarsOne: '1 stella',
    reviewStarsMany: '{count} stelle'
  } : {};
  const reviewText = (key, fallback) => window.ieGeekListingStrings?.[key] || defaultReviewStrings[key] || fallback;
  const ratingLabel = rating => {
    if (!rating) return reviewText('reviewAllRatings', 'All ratings');
    if (rating === '1') return reviewText('reviewStarsOne', '1 star');
    return reviewText('reviewStarsMany', '{count} stars').replaceAll('{count}', rating);
  };
  const syncRatingFilter = (widget, filters) => {
    const rows = ratingRows(widget);
    if (rows.length !== 5 || !rows.every(row => /^[1-5]$/.test(rowRating(row)))) {
      widget.classList.remove('listing-review-rating-filter-ready');
      filters.querySelector('.listing-review-filter')?.remove();
      widget.querySelectorAll('.listing-review-native-filter-panel').forEach(panel => panel.classList.remove('listing-review-native-filter-panel'));
      return;
    }
    rows.forEach(row => {
      // Preserve Judge.me's numeric text, which is also used to find the clicked row.
      const rating = row.querySelector('.jm-histogram-row__rating');
      const label = ratingLabel(rowRating(row));
      if (rating.dataset.listingRatingLabel !== label) rating.dataset.listingRatingLabel = label;
    });
    let control = filters.querySelector('.listing-review-filter');
    if (!control) {
      control = document.createElement('label');
      control.className = 'listing-review-filter';
      const caption = document.createElement('span');
      caption.className = 'listing-review-filter__caption';
      const field = document.createElement('listing-select');
      field.className = 'listing-review-filter__field';
      const value = document.createElement('span');
      value.className = 'listing-review-filter__value';
      value.setAttribute('aria-hidden', 'true');
      const select = document.createElement('select');
      select.className = 'listing-review-filter__select';
      ['', '5', '4', '3', '2', '1'].forEach(rating => select.add(new Option(ratingLabel(rating), rating)));
      select.addEventListener('change', () => {
        const current = selectedRating(widget);
        // Clicking the selected histogram row again clears only the rating filter.
        const target = ratingRows(widget).find(row => rowRating(row) === (select.value || current));
        if (select.value !== current && target?.getAttribute('aria-disabled') !== 'true') target?.click();
        scheduleUpdate();
      });
      field.append(value, select);
      control.append(caption, field);
      filters.append(control);
    }
    const select = control.querySelector('select');
    // Judge.me can rebuild its filters after loading or changing a rating.
    // Keep both the native select and its enhanced menu on the active locale.
    const caption = control.querySelector('.listing-review-filter__caption');
    const filterBy = reviewText('reviewFilterBy', 'Filter by');
    if (caption.textContent !== filterBy) caption.textContent = filterBy;
    if (select.getAttribute('aria-label') !== filterBy) select.setAttribute('aria-label', filterBy);
    [...select.options].forEach(option => {
      const label = ratingLabel(option.value);
      if (option.textContent !== label) option.textContent = label;
    });
    const current = selectedRating(widget);
    if (select.value !== current) select.value = current;
    const text = select.selectedOptions[0].textContent;
    const value = control.querySelector('.listing-review-filter__value');
    if (value.textContent !== text) value.textContent = text;
    select.disabled = rows.every(row => row.getAttribute('aria-disabled') === 'true');
    select.closest('listing-select')?.refresh?.();
    widget.classList.add('listing-review-rating-filter-ready');
    // The App opens this panel when its histogram is used. Keep its state and
    // listeners intact, but remove the redundant panel (including its grid slot).
    widget.querySelectorAll('.jm-filter-panel').forEach(panel => panel.parentElement.classList.add('listing-review-native-filter-panel'));
  };
  const update = () => {
    document.querySelectorAll('.jdgm-review-widget').forEach(widget => {
      widget.querySelectorAll('.jm-review-item .jm-star-rating__font-icon').forEach(star => {
        const code = star.textContent.trim().codePointAt(0);
        const fill = code === 0xe001 ? '0%' : code === 0xe002 ? '50%' : '100%';
        if (star.style.getPropertyValue('--listing-star-fill') !== fill) star.style.setProperty('--listing-star-fill', fill);
      });
      const summary = widget.querySelector('.jm-average-rating-display > .jm-cluster');
      if (!summary) return;
      const texts = summary.querySelectorAll(':scope > .jm-text');
      const rating = Number.parseFloat(texts[0]?.textContent);
      if (Number.isFinite(rating)) {
        let stars = summary.querySelector('.listing-review-summary-stars');
        if (!stars) {
          stars = document.createElement('span');
          stars.className = 'listing-review-summary-stars';
          stars.setAttribute('role', 'img');
          summary.append(stars);
        }
        const value = `${Math.max(0, Math.min(5, rating)) / 5 * 100}%`;
        if (stars.style.getPropertyValue('--listing-review-rating') !== value) stars.style.setProperty('--listing-review-rating', value);
        const label = `${rating} out of 5 stars`;
        if (stars.getAttribute('aria-label') !== label) stars.setAttribute('aria-label', label);
      }
      const filters = widget.querySelector('.jm-filters');
      if (filters && texts[1]) {
        let count = filters.querySelector('.listing-review-count');
        if (!count) {
          count = document.createElement('span');
          count.className = 'listing-review-count';
          filters.prepend(count);
        }
        if (count.textContent !== texts[1].textContent) count.textContent = texts[1].textContent;
        syncRatingFilter(widget, filters);
      }
    });
  };
  let scheduled = false;
  const scheduleUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; update(); });
  };
  const observer = new MutationObserver(scheduleUpdate);
  const observeReviews = root => {
    const widgets = [...root.querySelectorAll('.listing-reviews, .jdgm-review-widget')];
    if (root.matches?.('.listing-reviews, .jdgm-review-widget')) widgets.push(root);
    widgets.forEach(widget => observer.observe(widget, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-disabled'] }));
    update();
  };
  document.addEventListener('shopify:section:load', event => observeReviews(event.target));
  observeReviews(document);
})();
